import { VersionedServiceConfig } from '../../types';

// Full set of all possible size buckets (for extensibility)
export const BLUEBERRY_SIZE_BUCKETS_ALL = [
  'dummy',
  'very_small',
  'small',
  'large',
  'very_large',
  'extra_large', // Available v16+
  'ultra_large', // Available v16+
  'unknown',
] as const;

// Legacy buckets for v12-15
export const BLUEBERRY_SIZE_BUCKETS_LEGACY = [
  'dummy',
  'very_small',
  'small',
  'large',
  'very_large',
  'unknown',
] as const;

// Extended buckets for v16+
export const BLUEBERRY_SIZE_BUCKETS_EXTENDED = [
  'dummy',
  'very_small',
  'small',
  'large',
  'very_large',
  'extra_large',
  'ultra_large',
  'unknown',
] as const;

// Keep the old export for backward compatibility
export const BLUEBERRY_SIZE_BUCKETS = BLUEBERRY_SIZE_BUCKETS_LEGACY;

export type SizeBucketAll = (typeof BLUEBERRY_SIZE_BUCKETS_ALL)[number];
export type SizeBucketLegacy = (typeof BLUEBERRY_SIZE_BUCKETS_LEGACY)[number];
export type SizeBucketExtended =
  (typeof BLUEBERRY_SIZE_BUCKETS_EXTENDED)[number];
export type SizeBucket = SizeBucketLegacy | SizeBucketExtended;

// Flexible label map - only includes labels that exist for that version
export type SizeBucketLabelMap = Partial<Record<SizeBucketAll, string>>;

/**
 * Get the appropriate size buckets array for a given version
 */
export const getSizeBucketsForVersion = (
  version: string
): readonly string[] => {
  const versionNum = parseInt(version.split('.')[0]);
  if (versionNum >= 16) {
    return BLUEBERRY_SIZE_BUCKETS_EXTENDED;
  }
  return BLUEBERRY_SIZE_BUCKETS_LEGACY;
};

/**
 * Get only the non-dummy buckets for a version (used for data processing)
 */
export const getActiveSizeBucketsForVersion = (
  version: string
): readonly string[] => {
  return getSizeBucketsForVersion(version).filter(
    (bucket) => bucket !== 'dummy'
  );
};

/**
 * Blueberry size bucket label mappings by service version
 * Note: Only includes buckets that are actually available for each version
 */
export const blueberrySizeBucketLabelsByVersion: VersionedServiceConfig<SizeBucketLabelMap> =
  {
    '12-15': {
      // Only include buckets available in v12-15
      dummy: '',
      very_small: '<7mm',
      small: '7-12mm',
      large: '12-17mm',
      very_large: '>17mm', // Original range for v12-15
      unknown: 'Unmeasurable',
      // extra_large and ultra_large deliberately omitted
    },
    '16-': {
      // Include all buckets available in v16+
      dummy: '',
      very_small: '4-7mm',
      small: '7-12mm',
      large: '12-17mm',
      very_large: '17-22mm', // Updated range for v16+
      extra_large: '22-29mm', // NEW: Available from v16+
      ultra_large: '29mm-35mm', // NEW: Available from v16+
      unknown: 'Unmeasurable',
    },
  };
